// ARQUIVO ATUALIZADO E CORRIGIDO
import { useState, useEffect, useCallback, useRef } from 'react';

const useDraggableResizable = (initialPos, initialSize, boundaryRef, options = {}) => {
    const { 
        minW = 50, minH = 50, 
        maxW = Infinity, maxH = Infinity, 
        lockAspectRatio = false 
    } = options;

    const elementRef = useRef(null);
    const [position, setPosition] = useState(initialPos);
    const [size, setSize] = useState(initialSize);
    
    // --- INÍCIO DA ALTERAÇÃO ---
    // Este useEffect garante que, se a posição inicial for calculada dinamicamente,
    // o hook reflita essa mudança.
    useEffect(() => {
        setPosition(initialPos);
    }, [initialPos]);

    const actionState = useRef({
        isDragging: false, isResizing: false,
        initialMousePos: { x: 0, y: 0 },
        initialElementPos: { x: 0, y: 0 },
        initialElementSize: { width: 0, height: 0 },
        aspectRatio: 1,
    }).current;
    // --- FIM DA ALTERAÇÃO ---

    const handleMouseMove = useCallback((e) => {
        if (!actionState.isDragging && !actionState.isResizing) return;
        
        e.preventDefault(); // Impede seleção de texto indesejada durante o arraste

        const dx = e.clientX - actionState.initialMousePos.x;
        const dy = e.clientY - actionState.initialMousePos.y;
        
        const boundaryEl = boundaryRef?.current;
        const element = elementRef.current;

        if (actionState.isDragging && element) {
            let newX = actionState.initialElementPos.x + dx;
            let newY = actionState.initialElementPos.y + dy;
            
            if (boundaryEl) {
                newX = Math.max(0, newX);
                newY = Math.max(0, newY);
                newX = Math.min(newX, boundaryEl.offsetWidth - element.offsetWidth);
                newY = Math.min(newY, boundaryEl.offsetHeight - element.offsetHeight);
            }
            
            setPosition({ x: newX, y: newY });
        }

        if (actionState.isResizing) {
            let newHeight = Math.max(actionState.initialElementSize.height + dy, minH);
            newHeight = Math.min(newHeight, maxH);

            let newWidth;
            if (lockAspectRatio && actionState.aspectRatio) {
                newWidth = newHeight * actionState.aspectRatio;
            } else {
                newWidth = Math.max(actionState.initialElementSize.width + dx, minW);
                newWidth = Math.min(newWidth, maxW);
            }
            
            setSize({ width: newWidth, height: newHeight });
        }
    }, [actionState, boundaryRef, minW, minH, maxW, maxH, lockAspectRatio]);

    const handleMouseUp = useCallback(() => {
        actionState.isDragging = false;
        actionState.isResizing = false;
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove, actionState]);

    useEffect(() => {
        const currentElement = elementRef.current;
        if (!currentElement) return;

        const handleMouseDown = (e) => {
            const action = e.target.getAttribute('data-action');
            if (!action || e.button !== 0) return;

            e.preventDefault();
            e.stopPropagation();

            actionState.isDragging = action === 'drag';
            actionState.isResizing = action === 'resize';
            actionState.initialMousePos = { x: e.clientX, y: e.clientY };
            actionState.initialElementPos = { x: currentElement.offsetLeft, y: currentElement.offsetTop };
            actionState.initialElementSize = { width: currentElement.offsetWidth, height: currentElement.offsetHeight };
            if (lockAspectRatio && currentElement.offsetHeight > 0) {
                actionState.aspectRatio = currentElement.offsetWidth / currentElement.offsetHeight;
            }

            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        };

        currentElement.addEventListener('mousedown', handleMouseDown);

        return () => {
            currentElement.removeEventListener('mousedown', handleMouseDown);
        };
    }, [actionState, lockAspectRatio, handleMouseMove, handleMouseUp]);

    return { elementRef, position, size };
};

export default useDraggableResizable;